import React from "react";
import { Box, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import LinkIcon from "@mui/icons-material/Link";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";

const PRIVACY_META = {
  public: { Icon: PublicIcon, label: "Public" },
  unlisted: { Icon: LinkIcon, label: "Unlisted" },
  private: { Icon: LockIcon, label: "Private" },
};

const Youtube = ({
  selectedAccounts,
  formattedTags,
  accResp,
  selectedImages,
  selectedIndex,
  avatars,
  postTitle,
  postText,
  youtubePrivacy = "public",
}) => {
  const privacyInfo = PRIVACY_META[youtubePrivacy] || PRIVACY_META.public;
  const PrivacyIcon = privacyInfo.Icon;

  const currentAccountId = selectedAccounts[selectedIndex];
  const currentAccount = accResp.find((acc) => acc.id === currentAccountId);
  const channelName = currentAccount?.username || null;
  const avatar = avatars[accResp.findIndex((acc) => acc.id === currentAccountId)];

  const thumbnail = selectedImages.length > 0 ? selectedImages[0] : null;
  const hasContent = postTitle || postText;

  return (
    <Box className="tab-pane h-full" sx={{ padding: "8px 12px 16px" }}>
      {/* Video thumbnail area */}
      <Box
        sx={{
          width: "100%",
          aspectRatio: "16 / 9",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor: "#0f0f0f",
          position: "relative",
          mb: "10px",
        }}>
        {thumbnail ? (
          <img
            src={thumbnail.props.src}
            alt="thumbnail"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#1a1a1a",
            }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <PlayArrowIcon sx={{ color: "#fff", fontSize: 30 }} />
            </Box>
          </Box>
        )}

        {/* Privacy badge */}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            display: "flex",
            alignItems: "center",
            gap: "4px",
            backgroundColor: "rgba(0,0,0,0.72)",
            borderRadius: "4px",
            padding: "2px 7px",
          }}>
          <PrivacyIcon sx={{ fontSize: 11, color: "#fff" }} />
          <Typography sx={{ fontSize: "11px", color: "#fff", fontWeight: 500 }}>
            {privacyInfo.label}
          </Typography>
        </Box>

        {/* Duration placeholder */}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            backgroundColor: "rgba(0,0,0,0.72)",
            borderRadius: "4px",
            padding: "1px 5px",
          }}>
          <Typography sx={{ fontSize: "11px", color: "#fff", fontWeight: 500 }}>
            0:00
          </Typography>
        </Box>
      </Box>

      {/* Title + menu */}
      <Box sx={{ display: "flex", gap: "8px", alignItems: "flex-start", mb: "8px" }}>
        {/* Channel avatar */}
        {selectedAccounts.length > 0 ? (
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              overflow: "hidden",
              flexShrink: 0,
              backgroundColor: "#FF0000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            {avatar ? (
              <img
                src={avatar}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
                {channelName ? channelName.charAt(0).toUpperCase() : "Y"}
              </Typography>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              backgroundColor: "var(--background-preview)",
              flexShrink: 0,
            }}
          />
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Video title */}
          {postTitle ? (
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "var(--text-color)",
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                mb: "4px",
              }}>
              {postTitle}
            </Typography>
          ) : (
            <Box>
              <Box
                sx={{
                  width: "90%",
                  height: 12,
                  backgroundColor: "var(--background-preview)",
                  borderRadius: 1,
                  mb: "5px",
                }}
              />
              <Box
                sx={{
                  width: "60%",
                  height: 12,
                  backgroundColor: "var(--background-preview)",
                  borderRadius: 1,
                }}
              />
            </Box>
          )}

          {/* Channel name + meta */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
            {channelName ? (
              <Typography sx={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.6 }}>
                {channelName}
              </Typography>
            ) : (
              <Box
                sx={{
                  width: 70,
                  height: 10,
                  backgroundColor: "var(--background-preview)",
                  borderRadius: 1,
                }}
              />
            )}
            <Typography sx={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.4 }}>
              · 0 views · just now
            </Typography>
          </Box>
        </Box>

        <MoreVertIcon sx={{ fontSize: 18, color: "var(--text-color)", opacity: 0.5, flexShrink: 0 }} />
      </Box>

      {/* Action bar */}
      <Box
        sx={{
          display: "flex",
          gap: "6px",
          mb: "10px",
          overflowX: "auto",
          pb: "2px",
        }}>
        {[
          { Icon: ThumbUpOutlinedIcon, label: "Like" },
          { Icon: ThumbDownOutlinedIcon, label: "Dislike" },
          { Icon: ShareOutlinedIcon, label: "Share" },
        ].map(({ Icon, label }) => (
          <Box
            key={label}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: "5px 12px",
              borderRadius: "20px",
              backgroundColor: "var(--background-preview)",
              flexShrink: 0,
            }}>
            <Icon sx={{ fontSize: 15, color: "var(--text-color)", opacity: 0.7 }} />
            <Typography sx={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.7 }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Description */}
      {hasContent ? (
        <Box
          sx={{
            backgroundColor: "var(--background-preview)",
            borderRadius: "10px",
            padding: "10px 12px",
          }}>
          {postText && (
            <Typography
              sx={{
                fontSize: "12px",
                color: "var(--text-color)",
                opacity: 0.75,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                mb: formattedTags ? "4px" : 0,
              }}>
              {postText}
            </Typography>
          )}
          {formattedTags && (
            <Typography
              sx={{
                fontSize: "12px",
                color: "#FF0000",
                lineHeight: 1.5,
                wordBreak: "break-word",
              }}>
              {formattedTags}
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: "var(--background-preview)",
            borderRadius: "10px",
            padding: "10px 12px",
          }}>
          {[90, 75, 50].map((w, i) => (
            <Box
              key={i}
              sx={{
                width: `${w}%`,
                height: 10,
                backgroundColor: "rgba(255,255,255,0.07)",
                borderRadius: 1,
                mb: i < 2 ? "6px" : 0,
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Youtube;
